import React, { useState, useRef, useEffect } from 'react';
import {
    X, Download, Share2, Mail, Sparkles, FileText, Brain, Rocket, CheckCircle2, Info,
    Briefcase, Award, Languages, GraduationCap, AlertTriangle, Phone, MapPin,
    Linkedin, Copy, ExternalLink, Loader2, Pencil, Save, Clock, Euro,
    ChevronDown, ChevronUp, Presentation, EyeOff,
} from 'lucide-react';
import type { TalentProfile } from '../../types';

// Local type extension matching the one in TalentPoolView
type DisplayProfile = TalentProfile & {
    status?: 'analyzing' | 'ready';
    calculatedScore?: number;
};

type AvailabilityStatus = 'disponible' | 'listening' | 'unavailable';
type AvailabilityType = 'freelance' | 'cdi' | 'both';

interface EditedAvailability {
    availability_status: AvailabilityStatus | null;
    availability_type: AvailabilityType | null;
    availability_date: string | null;
    tjm_min: number | null;
    tjm_max: number | null;
}

interface CandidateDetailPanelProps {
    profile: DisplayProfile;
    isContactModalOpen: boolean;
    setIsContactModalOpen: (open: boolean) => void;
    isEditingContact: boolean;
    setIsEditingContact: (editing: boolean) => void;
    editedContact: any;
    setEditedContact: (contact: any) => void;
    isSavingContact: boolean;
    isShareLoading: boolean;
    onClose: () => void;
    onExportPDF: (isAnonymized: boolean) => void;
    isExportingPDF?: boolean;
    onOpenPresentation: (isAnonymized: boolean) => void;
    onShare: (isAnonymized: boolean) => void;
    onGoToMatching: () => void;
    onSaveContact: () => void;
    onUploadPhoto: (file: File) => Promise<void>;
    getSkillBadgeStyle: (type: 'technical' | 'methods' | 'soft') => string;
    isEditingAvailability: boolean;
    setIsEditingAvailability: (v: boolean) => void;
    editedAvailability: EditedAvailability;
    setEditedAvailability: (v: EditedAvailability) => void;
    isSavingAvailability: boolean;
    onSaveAvailability: () => void;
}

const AVAILABILITY_STATUS_OPTIONS: { value: AvailabilityStatus; label: string; color: string; dot: string }[] = [
    { value: 'disponible', label: 'Disponible pour rencontrer un client', color: 'border-emerald-400 bg-emerald-50 text-emerald-800', dot: 'bg-emerald-500' },
    { value: 'listening', label: 'À l\'écoute d\'opportunités', color: 'border-amber-400 bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
    { value: 'unavailable', label: 'Indisponible pour un nouveau job', color: 'border-slate-300 bg-slate-50 text-slate-500', dot: 'bg-slate-400' },
];

const AVAILABILITY_TYPE_OPTIONS: { value: AvailabilityType; label: string }[] = [
    { value: 'freelance', label: 'Freelance' },
    { value: 'cdi', label: 'CDI' },
    { value: 'both', label: 'Les 2' },
];

const statusConfig = (status: AvailabilityStatus | null | undefined) =>
    AVAILABILITY_STATUS_OPTIONS.find(o => o.value === status);

const CandidateDetailPanel: React.FC<CandidateDetailPanelProps> = ({
    profile,
    isContactModalOpen,
    setIsContactModalOpen,
    isEditingContact,
    setIsEditingContact,
    editedContact,
    setEditedContact,
    isSavingContact,
    isShareLoading,
    onClose,
    onExportPDF,
    isExportingPDF = false,
    onOpenPresentation,
    onShare,
    onGoToMatching,
    onSaveContact,
    onUploadPhoto,
    getSkillBadgeStyle,
    isEditingAvailability,
    setIsEditingAvailability,
    editedAvailability,
    setEditedAvailability,
    isSavingAvailability,
    onSaveAvailability,
}) => {
    const [availDropdownOpen, setAvailDropdownOpen] = useState(false);
    const availDropdownRef = useRef<HTMLDivElement>(null);
    const [ficheDropdownOpen, setFicheDropdownOpen] = useState(false);
    const ficheDropdownRef = useRef<HTMLDivElement>(null);
    const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
    const pdfDropdownRef = useRef<HTMLDivElement>(null);
    const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
    const [skillsExpanded, setSkillsExpanded] = useState(false);
    const shareDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (availDropdownRef.current && !availDropdownRef.current.contains(e.target as Node)) {
                setAvailDropdownOpen(false);
            }
            if (ficheDropdownRef.current && !ficheDropdownRef.current.contains(e.target as Node)) {
                setFicheDropdownOpen(false);
            }
            if (pdfDropdownRef.current && !pdfDropdownRef.current.contains(e.target as Node)) {
                setPdfDropdownOpen(false);
            }
            if (shareDropdownRef.current && !shareDropdownRef.current.contains(e.target as Node)) {
                setShareDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const currentStatus = statusConfig(profile.availability_status);

    return (
        <div id="candidate-profile-print" className="fixed inset-0 md:left-64 z-30 overflow-y-auto bg-white animate-in fade-in duration-300 flex flex-col">

                {/* 1. STICKY PROFILE TOOLBAR (Header) */}
                <div className="px-4 md:px-8 py-4 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-0 z-20 shadow-sm">
                    {/* Left: Back + Profile Identity */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors shrink-0"
                            title="Fermer la fiche"
                            aria-label="Fermer la fiche"
                        >
                            <X size={18} />
                        </button>
                        <label className="relative group cursor-pointer shrink-0" title="Changer la photo">
                            {profile.photo_url ? (
                                <img
                                    src={profile.photo_url}
                                    alt={profile.name}
                                    className="w-14 h-14 rounded-full object-cover shadow-md border-2 border-slate-100"
                                />
                            ) : (
                                <div className="w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center text-lg font-bold shadow-md border-2 border-slate-100">
                                    {profile.name.substring(0, 2)}
                                </div>
                            )}
                            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Pencil size={14} className="text-white" />
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                className="sr-only"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onUploadPhoto(file);
                                    e.target.value = '';
                                }}
                            />
                        </label>
                        <div>
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-bold text-slate-900">{profile.name}</h2>
                                {profile.updatedAt && (() => {
                                    const d = new Date(profile.updatedAt);
                                    const m = (new Date().getFullYear() - d.getFullYear()) * 12 + (new Date().getMonth() - d.getMonth());
                                    const c = m >= 6 ? 'text-red-500' : m >= 1 ? 'text-amber-500' : 'text-emerald-500';
                                    return <span className={`text-[11px] font-medium ${c}`}>· Mise à jour : {d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>;
                                })()}
                            </div>
                            <p className="text-slate-500 font-medium text-sm mt-0.5">{profile.role}</p>
                            {profile.role_fit_titles && profile.role_fit_titles.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                    {profile.role_fit_titles.slice(0, 3).map((title, i) => (
                                        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                                            {title}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Action Buttons */}
                    <div className="flex items-center gap-3">
                        {/* Group A: Utilities */}
                        <div className="flex items-center gap-2 border-r border-slate-200 pr-3 mr-1">
                            <div className="relative" ref={ficheDropdownRef}>
                                <button
                                    onClick={() => setFicheDropdownOpen(v => !v)}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
                                    title="Fiche de présentation"
                                >
                                    <Presentation size={16} />
                                    <span className="hidden lg:inline">Fiche</span>
                                    <ChevronDown size={12} className={`text-slate-400 transition-transform ${ficheDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {ficheDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-3 animate-in fade-in zoom-in-95 duration-100">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Fiche</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { onOpenPresentation(false); setFicheDropdownOpen(false); }}
                                                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                            >
                                                Nom
                                            </button>
                                            <button
                                                onClick={() => { onOpenPresentation(true); setFicheDropdownOpen(false); }}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors whitespace-nowrap"
                                            >
                                                <EyeOff size={13} /> Anonyme
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative" ref={pdfDropdownRef}>
                                <button
                                    onClick={() => setPdfDropdownOpen(v => !v)}
                                    disabled={isExportingPDF}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-60"
                                    title="Télécharger le CV en PDF"
                                >
                                    {isExportingPDF ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                    <span className="hidden lg:inline">CV</span>
                                    <ChevronDown size={12} className={`text-slate-400 transition-transform ${pdfDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {pdfDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-3 animate-in fade-in zoom-in-95 duration-100">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">CV</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { onExportPDF(false); setPdfDropdownOpen(false); }}
                                                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                            >
                                                Nom
                                            </button>
                                            <button
                                                onClick={() => { onExportPDF(true); setPdfDropdownOpen(false); }}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors whitespace-nowrap"
                                            >
                                                <EyeOff size={13} /> Anonyme
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative" ref={shareDropdownRef}>
                                <button
                                    onClick={() => setShareDropdownOpen(v => !v)}
                                    disabled={isShareLoading}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors disabled:opacity-60"
                                >
                                    {isShareLoading ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                                    <span className="hidden lg:inline">Partager</span>
                                    <ChevronDown size={12} className={`text-slate-400 transition-transform ${shareDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {shareDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-3 animate-in fade-in zoom-in-95 duration-100">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Partager</p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => { onShare(false); setShareDropdownOpen(false); }}
                                                className="flex-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors whitespace-nowrap"
                                            >
                                                Nom
                                            </button>
                                            <button
                                                onClick={() => { onShare(true); setShareDropdownOpen(false); }}
                                                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors whitespace-nowrap"
                                            >
                                                <EyeOff size={13} /> Anonyme
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Group B: Contact (Opens Modal) */}
                        <button
                            onClick={() => setIsContactModalOpen(true)}
                            className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-bold transition-colors ${isContactModalOpen ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-amber-500 text-amber-600 hover:bg-amber-50'}`}
                        >
                            <Mail size={16} />
                            Contacter
                        </button>

                        {/* Group B2: Disponibilité dropdown */}
                        <div className="relative" ref={availDropdownRef}>
                            <button
                                onClick={() => { setAvailDropdownOpen(v => !v); setIsEditingAvailability(false); }}
                                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-semibold transition-colors ${availDropdownOpen ? 'bg-slate-100 border-slate-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
                            >
                                {currentStatus
                                    ? <span className={`w-2 h-2 rounded-full shrink-0 ${currentStatus.dot}`} />
                                    : <Clock size={14} className="text-slate-400" />}
                                <span className="hidden sm:inline text-slate-700">
                                    {currentStatus
                                        ? currentStatus.value === 'disponible' ? 'Disponible'
                                          : currentStatus.value === 'listening' ? 'À l\'écoute'
                                          : 'Indisponible'
                                        : 'Disponibilité'}
                                </span>
                                {(profile.tjm_min || profile.tjm_max) && (
                                    <span className="hidden lg:inline text-[11px] font-bold text-blue-600 bg-blue-50 border border-blue-100 rounded px-1.5 py-0.5">
                                        {profile.tjm_min && profile.tjm_max
                                            ? `${profile.tjm_min}–${profile.tjm_max}€/j`
                                            : profile.tjm_min ? `≥${profile.tjm_min}€/j` : `≤${profile.tjm_max}€/j`}
                                    </span>
                                )}
                                <ChevronDown size={14} className={`text-slate-400 transition-transform ${availDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {availDropdownOpen && (
                                <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-4 bg-slate-50">
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Disponibilité & TJM</span>
                                        <div className="flex items-center gap-2 shrink-0">
                                        {!isEditingAvailability ? (
                                            <button onClick={() => setIsEditingAvailability(true)} className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 border border-slate-200 bg-white rounded-lg px-2.5 py-1 transition-colors">
                                                <Pencil size={11} /> Modifier
                                            </button>
                                        ) : (
                                            <>
                                                <button onClick={() => setIsEditingAvailability(false)} className="text-xs font-medium text-slate-500 hover:text-slate-800 border border-slate-200 bg-white rounded-lg px-2.5 py-1 transition-colors" disabled={isSavingAvailability}>Annuler</button>
                                                <button
                                                    onClick={() => { onSaveAvailability(); setAvailDropdownOpen(false); }}
                                                    disabled={isSavingAvailability}
                                                    className="flex items-center gap-1.5 text-xs font-bold bg-amber-600 text-white px-3 py-1 rounded-lg transition-colors hover:bg-amber-700"
                                                >
                                                    {isSavingAvailability ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                                                    Enregistrer
                                                </button>
                                            </>
                                        )}
                                        </div>
                                    </div>

                                    <div className="p-4 space-y-4">
                                        {isEditingAvailability ? (
                                            <>
                                                {/* Status radio */}
                                                <div className="space-y-1.5">
                                                    {AVAILABILITY_STATUS_OPTIONS.map(opt => (
                                                        <label
                                                            key={opt.value}
                                                            className={`flex items-center gap-2.5 p-2.5 rounded-lg border-2 cursor-pointer transition-all ${editedAvailability.availability_status === opt.value ? opt.color : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
                                                        >
                                                            <input type="radio" name="avail_status" value={opt.value} checked={editedAvailability.availability_status === opt.value} onChange={() => setEditedAvailability({ ...editedAvailability, availability_status: opt.value })} className="sr-only" />
                                                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${opt.dot}`} />
                                                            <span className="text-[12px] font-medium leading-tight">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>

                                                {/* Date */}
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Disponible à partir du</p>
                                                    <input
                                                        type="date"
                                                        value={editedAvailability.availability_date ?? ''}
                                                        onChange={e => setEditedAvailability({ ...editedAvailability, availability_date: e.target.value || null })}
                                                        className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
                                                    />
                                                </div>

                                                {/* TJM */}
                                                <div className="space-y-1">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Euro size={9} /> TJM freelance (€/jour)</p>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1">
                                                            <input type="number" min={0} placeholder="Min" value={editedAvailability.tjm_min ?? ''} onChange={e => setEditedAvailability({ ...editedAvailability, tjm_min: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                                                        </div>
                                                        <span className="text-slate-300 text-sm">–</span>
                                                        <div className="flex-1">
                                                            <input type="number" min={0} placeholder="Max" value={editedAvailability.tjm_max ?? ''} onChange={e => setEditedAvailability({ ...editedAvailability, tjm_max: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* Read view */}
                                                {currentStatus ? (
                                                    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border ${currentStatus.color}`}>
                                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${currentStatus.dot}`} />
                                                        <span className="text-[13px] font-semibold">{currentStatus.label}</span>
                                                    </div>
                                                ) : (
                                                    <p className="text-[12px] text-slate-400 italic text-center py-2">Statut non renseigné</p>
                                                )}
                                                {profile.availability_date && (
                                                    <div className="flex items-center gap-2 text-[12px] text-slate-600">
                                                        <Clock size={12} className="text-slate-400" />
                                                        Dispo le {new Date(profile.availability_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </div>
                                                )}
                                                {(profile.tjm_min || profile.tjm_max) && (
                                                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                                                        <Euro size={12} className="text-blue-500 shrink-0" />
                                                        <span className="text-[13px] font-bold text-blue-700">
                                                            {profile.tjm_min && profile.tjm_max ? `${profile.tjm_min} – ${profile.tjm_max} €/j` : profile.tjm_min ? `≥ ${profile.tjm_min} €/j` : `≤ ${profile.tjm_max} €/j`}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Group C: Matching (Primary) */}
                        <button
                            onClick={onGoToMatching}
                            className="flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 shadow-md shadow-amber-900/20 transition-all hover:-translate-y-0.5"
                        >
                            <Sparkles size={16} />
                            Matching
                        </button>

                    </div>
                </div>

                {/* 2. CONTENT — Two-column layout */}
                <div className="bg-slate-50/50 flex-1">
                    <div className="p-4 md:p-6 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-5 items-start">

                        {/* ── LEFT COLUMN (sticky on desktop) ── */}
                        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">

                            {/* Résumé */}
                            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <FileText size={16} className="text-slate-400" /> Résumé
                                </h3>
                                <p className="text-[13px] text-slate-800 leading-relaxed font-medium">
                                    {profile.profile_summary || profile.summary}
                                </p>
                            </div>

                            {/* Analyse Profil */}
                            <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-xl shadow-sm relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-3 opacity-10">
                                    <Brain size={50} className="text-indigo-900" />
                                </div>
                                <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-widest mb-2 flex items-center gap-2 relative z-10">
                                    <Sparkles size={16} className="text-indigo-600" /> Analyse Profil
                                </h3>
                                {profile.additional_insight
                                    ? <ul className="space-y-1.5 relative z-10">
                                        {profile.additional_insight
                                            .split(/\s*•\s*/)
                                            .map(s => s.trim())
                                            .filter(Boolean)
                                            .map((bullet, i) => (
                                                <li key={i} className="flex items-start gap-2 text-[13px] text-indigo-900 leading-relaxed font-medium">
                                                    <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                                    {bullet}
                                                </li>
                                            ))}
                                      </ul>
                                    : <p className="text-[13px] text-indigo-900 leading-relaxed font-medium relative z-10 italic opacity-60">
                                        Aucune analyse complémentaire disponible.
                                      </p>
                                }
                            </div>

                            {/* Facteur Différenciant */}
                            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                                <div className="flex items-center gap-2 text-amber-600 font-bold text-xs uppercase mb-2">
                                    <Rocket size={16} className="text-amber-500" />
                                    Facteur Différenciant
                                </div>
                                <p className="text-[13px] font-semibold text-slate-800 leading-relaxed mb-3">
                                    {profile.top_strengths || profile.differentiator}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {profile.badges.map(b => (
                                        <span key={b} className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded text-xs font-semibold text-slate-600">
                                            {b}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Top Réalisations */}
                            {profile.topAchievements && profile.topAchievements.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                                <h3 className="text-[14px] font-bold text-slate-900 mb-4 flex items-center gap-2">
                                    <Rocket size={16} className="text-amber-500" />
                                    Top Réalisations
                                </h3>
                                <ul className="space-y-3">
                                    {profile.topAchievements?.map((achievement, idx) => (
                                        <li key={idx} className="flex items-start gap-3">
                                            <div className="mt-1 w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                                                <CheckCircle2 size={11} />
                                            </div>
                                            <span className="text-[13px] text-slate-700 font-medium leading-relaxed">{achievement}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}

                            {/* Incohérences (Conditional) */}
                            {profile.alerts && profile.alerts.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-5 shadow-sm">
                                    <h3 className="text-[14px] font-bold text-red-900 mb-3 flex items-center gap-2">
                                        <AlertTriangle size={16} className="text-red-600" />
                                        Points d'attention
                                    </h3>
                                    <div className="space-y-2">
                                        {profile.alerts.map((alert, idx) => (
                                            <div key={idx} className="flex gap-3 text-red-800 bg-white/60 p-3 rounded-lg border border-red-100">
                                                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-red-500 shrink-0"></div>
                                                <span className="font-medium text-[13px] leading-relaxed">{alert}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Infos Clés */}
                            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                                <h3 className="text-[14px] font-bold text-slate-900 mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                                    <Award size={16} className="text-slate-400" />
                                    Infos Clés
                                </h3>
                                <div className="space-y-5">
                                    {/* Languages */}
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-wider">
                                            <Languages size={13} /> Langues
                                        </div>
                                        <ul className="flex flex-wrap gap-2">
                                            {profile.languages?.map((lang, idx) => (
                                                <li key={idx} className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded text-xs font-semibold text-slate-600">{lang}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    {/* Education */}
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-wider">
                                            <GraduationCap size={13} /> Formation
                                        </div>
                                        <ul className="space-y-2">
                                            {profile.education?.map((edu, idx) => (
                                                <li key={idx}>
                                                    <div className="font-bold text-[13px] text-slate-800">{edu.degree}</div>
                                                    <div className="text-[12px] text-slate-500">{[edu.school, edu.year].filter(Boolean).join(', ')}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {/* Certifications */}
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-slate-400 font-bold text-xs uppercase tracking-wider">
                                            <Award size={13} /> Certifications
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {profile.certifications?.map((cert, idx) => (
                                                <span key={idx} className="px-2.5 py-1 rounded bg-slate-100 text-slate-700 text-[12px] font-medium border border-slate-200">
                                                    {cert}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* ── RIGHT COLUMN ── */}
                        <div className="space-y-4">

                            {/* Skills Matrix */}
                            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                                <h3 className="text-[14px] font-bold text-slate-900 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
                                    <CheckCircle2 size={18} className="text-slate-400" />
                                    Matrice de Compétences
                                    {profile.skillsScored && (
                                        <span className="ml-auto flex items-center gap-3 text-[11px] font-normal normal-case tracking-normal">
                                            <span className="flex items-center gap-1 text-slate-500"><span className="w-2 h-2 rounded-full bg-slate-400"></span>Prouvé</span>
                                            <span className="flex items-center gap-1 text-slate-500"><span className="w-2 h-2 rounded-full border border-slate-400"></span>Déduit</span>
                                            <span className="group/info relative flex items-center">
                                                <Info size={15} className="text-slate-400 cursor-help" />
                                                <div className="absolute right-0 top-full mt-2 hidden group-hover/info:block z-50 w-72 text-left">
                                                    <div className="bg-slate-900 text-white text-[11px] leading-relaxed p-3 rounded-lg shadow-xl">
                                                        <p className="font-semibold mb-1">Comment ce niveau est calculé</p>
                                                        <p>Chaque compétence est notée de 0 à 10 par une lecture du CV reflétant l'expérience réelle : l'ancienneté de la dernière utilisation, le nombre de missions concernées et leur durée.</p>
                                                    </div>
                                                </div>
                                            </span>
                                        </span>
                                    )}
                                </h3>
                                {(() => {
                                    type Skill = { name: string; level: number; category?: string; evidence?: string; inferred?: boolean };
                                    const scored = !!profile.skillsScored;

                                    const groupByCategory = (skills: Skill[], othersLabel: string) => {
                                        const rawGroups: Record<string, Skill[]> = {};
                                        skills.forEach(s => {
                                            const cat = s.category || othersLabel;
                                            if (!rawGroups[cat]) rawGroups[cat] = [];
                                            rawGroups[cat].push(s);
                                        });

                                        const finalGroups: Record<string, Skill[]> = {};
                                        const othersGroup: Skill[] = [];

                                        Object.entries(rawGroups).forEach(([cat, catSkills]) => {
                                            if (catSkills.length === 1 && cat !== othersLabel) {
                                                othersGroup.push(...catSkills);
                                            } else {
                                                finalGroups[cat] = catSkills.sort((a, b) => b.level - a.level);
                                            }
                                        });

                                        const sorted = Object.entries(finalGroups)
                                            .sort((a, b) => Math.max(...b[1].map(s => s.level)) - Math.max(...a[1].map(s => s.level)));

                                        if (othersGroup.length > 0) {
                                            sorted.push([othersLabel, othersGroup.sort((a, b) => b.level - a.level)]);
                                        }

                                        return sorted;
                                    };

                                    const marker = (s: Skill) =>
                                        s.inferred === false
                                            ? <span className="w-2 h-2 rounded-full bg-slate-400 shrink-0" title="Prouvé"></span>
                                            : s.inferred === true
                                                ? <span className="w-2 h-2 rounded-full border border-slate-400 shrink-0" title="Déduit"></span>
                                                : null;

                                    const renderSkill = (s: Skill, barColor: string) => (
                                        <div key={s.name} className="group/skill relative" title={s.evidence || undefined}>
                                            <span className="text-[13px] font-semibold text-slate-700 mb-1.5 flex items-center justify-between gap-2">
                                                <span className="flex items-center gap-1.5 min-w-0">{marker(s)}<span className="truncate">{s.name}</span></span>
                                                {scored && <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{Math.round(s.level / 10)}/10</span>}
                                            </span>
                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${s.level}%` }}></div>
                                            </div>
                                            {s.evidence && (
                                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover/skill:block z-50 max-w-xs">
                                                    <div className="bg-slate-900 text-white text-[11px] leading-relaxed px-3 py-2 rounded-lg shadow-xl">
                                                        {s.evidence}
                                                        <div className="absolute left-4 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-900"></div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );

                                    const renderColumn = (
                                        skills: Skill[],
                                        label: string,
                                        othersLabel: string,
                                        barColor: string,
                                        accentColor: string
                                    ) => {
                                        const header = (
                                            <div className="flex items-center gap-3 mb-1">
                                                <span className={`w-10 h-1 rounded ${accentColor}`}></span>
                                                <h4 className="font-bold text-[14px] text-slate-900 uppercase tracking-wide">{label}</h4>
                                            </div>
                                        );

                                        // Nouveau scoring : liste simple triée par niveau, plafonnée à 6 (voir plus)
                                        if (scored) {
                                            const sorted = [...skills].sort((a, b) => b.level - a.level);
                                            const shown = skillsExpanded ? sorted : sorted.slice(0, 6);
                                            return (
                                                <div className="space-y-5">
                                                    {header}
                                                    <div className="space-y-3">
                                                        {shown.length ? shown.map(s => renderSkill(s, barColor)) : <span className="text-[12px] text-slate-400 italic">Aucune</span>}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        // Ancien affichage (summary_json) : groupé par sous-catégorie
                                        const categories = groupByCategory(skills, othersLabel);
                                        return (
                                            <div className="space-y-5">
                                                {header}
                                                {categories.map(([catName, catSkills]) => (
                                                    <div key={catName} className="space-y-3">
                                                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{catName}</span>
                                                        {catSkills.map(s => renderSkill(s, barColor))}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    };

                                    const hasMore = scored && (profile.skills.technical.length > 6 || profile.skills.methods.length > 6 || profile.skills.soft.length > 6);
                                    return (
                                        <>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                                            {renderColumn(profile.skills.technical, 'Technique', 'Autres Technique', 'bg-blue-500', 'bg-blue-500')}
                                            {renderColumn(profile.skills.methods, 'Méthodes', 'Autres Méthodes', 'bg-emerald-500', 'bg-emerald-500')}
                                            {renderColumn(profile.skills.soft, 'Soft Skills', 'Autres Soft Skills', 'bg-amber-500', 'bg-amber-500')}
                                        </div>
                                        {hasMore && (
                                            <button
                                                onClick={() => setSkillsExpanded(v => !v)}
                                                className="mt-6 mx-auto flex items-center gap-1.5 text-[13px] font-medium text-slate-500 hover:text-slate-900 transition-colors"
                                            >
                                                {skillsExpanded ? 'Réduire' : 'Voir toutes les compétences'}
                                                {skillsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Timeline */}
                            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                                <h3 className="text-[14px] font-bold text-slate-900 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
                                    <Briefcase size={18} className="text-slate-400" />
                                    Parcours & Expérience
                                </h3>
                                <div className="relative border-l-2 border-slate-100 ml-4 space-y-8">
                                    {profile.timeline.map((item, idx) => (
                                        <div key={idx} className="ml-10 relative group">
                                            <div className="absolute -left-[49px] top-1.5 w-5 h-5 rounded-full border-4 border-white bg-slate-300 group-hover:bg-amber-500 transition-colors shadow-sm"></div>
                                            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between mb-2">
                                                <h4 className="font-bold text-slate-800 text-[13px]">{item.role}</h4>
                                                <span className="text-xs text-slate-600 font-bold bg-slate-100 px-3 py-1 rounded-full border border-slate-200">{item.year}</span>
                                            </div>
                                            <p className="text-[13px] font-semibold text-slate-600 mb-3">
                                                {item.company}
                                            </p>
                                            {item.summary && (
                                                <ul className="mb-3 space-y-1">
                                                    {(item.summary.includes('•')
                                                        ? item.summary.split(/\s*•\s*/).map(s => s.trim()).filter(Boolean)
                                                        : item.summary.split('. ').map(s => s.trim()).filter(Boolean)
                                                    ).map((sentence, sIdx) => (
                                                        <li key={sIdx} className="flex items-start gap-2 text-[13px] text-slate-600 leading-relaxed">
                                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0"></span>
                                                            {sentence.endsWith('.') ? sentence : `${sentence}.`}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            <div className="flex flex-wrap gap-2">
                                                {item.tags.map(t => (
                                                    <span key={t} className="px-3 py-1 rounded-md text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">{t}</span>
                                                ))}
                                                {item.methodTags?.map(t => (
                                                    <span key={t} className="px-3 py-1 rounded-md text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">{t}</span>
                                                ))}
                                                {item.softTags?.map(t => (
                                                    <span key={t} className="px-3 py-1 rounded-md text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">{t}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* CONTACT MODAL INTERACTION */}
                {isContactModalOpen && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => e.stopPropagation()}>
                        {/* Dimmed Backdrop */}
                        <div
                            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                            onClick={() => { setIsContactModalOpen(false); setIsEditingContact(false); }}
                        ></div>

                        {/* Modal Card */}
                        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md z-10 overflow-hidden animate-in zoom-in-95 duration-200">
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                                <h3 className="font-bold text-lg text-slate-800">
                                    {isEditingContact ? 'Modifier Coordonnées' : 'Coordonnées du Candidat'}
                                </h3>
                                <div className="flex items-center gap-2">
                                    {!isEditingContact && (
                                        <button
                                            onClick={() => setIsEditingContact(true)}
                                            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-800 transition-colors"
                                            title="Modifier"
                                        >
                                            <Pencil size={18} />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setIsContactModalOpen(false); setIsEditingContact(false); }}
                                        className="p-1 rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-4">
                                {/* Phone */}
                                <div className={`flex items-center gap-4 p-3 rounded-xl transition-colors border ${isEditingContact ? 'bg-slate-50 border-slate-200' : 'hover:bg-slate-50 border-transparent group'}`}>
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                                        <Phone size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Téléphone</div>
                                        {isEditingContact ? (
                                            <input
                                                type="tel"
                                                value={editedContact.phone}
                                                onChange={(e) => setEditedContact({ ...editedContact, phone: e.target.value })}
                                                className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                placeholder="+33 6..."
                                            />
                                        ) : (
                                            <div className="flex items-center justify-between">
                                                <a href={`tel:${profile.contactInfo?.phone}`} className="text-slate-900 font-medium hover:text-emerald-600 hover:underline">
                                                    {profile.contactInfo?.phone || 'Non renseigné'}
                                                </a>
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(profile.contactInfo?.phone || '')}
                                                    className="text-slate-300 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-all"
                                                    title="Copier"
                                                >
                                                    <Copy size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Email (Read-Only even in Edit Mode) */}
                                <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl transition-colors group border border-transparent hover:border-slate-200">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                                        <Mail size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Email {isEditingContact && '(Non modifiable)'}</div>
                                        <div className="flex items-center justify-between">
                                            <a href={`mailto:${profile.contactInfo?.email}`} className="text-slate-900 font-medium hover:text-blue-600 hover:underline">
                                                {profile.contactInfo?.email || 'Non renseigné'}
                                            </a>
                                            <button
                                                onClick={() => navigator.clipboard.writeText(profile.contactInfo?.email || '')}
                                                className="text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all"
                                                title="Copier"
                                            >
                                                <Copy size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Location */}
                                <div className={`flex items-center gap-4 p-3 rounded-xl transition-colors border ${isEditingContact ? 'bg-slate-50 border-slate-200' : 'hover:bg-slate-50 border-transparent group'}`}>
                                    <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                                        <MapPin size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Localisation</div>
                                        {isEditingContact ? (
                                            <input
                                                type="text"
                                                value={editedContact.location_city}
                                                onChange={(e) => setEditedContact({ ...editedContact, location_city: e.target.value })}
                                                className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                                                placeholder="Paris, France"
                                            />
                                        ) : (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(profile.contactInfo?.location_city || '')}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-slate-900 font-medium hover:text-slate-700 hover:underline flex items-center gap-2"
                                            >
                                                {profile.contactInfo?.location_city || 'Non renseigné'}
                                                <ExternalLink size={12} className="opacity-50" />
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {/* LinkedIn */}
                                <div className={`flex items-center gap-4 p-3 rounded-xl transition-colors border ${isEditingContact ? 'bg-slate-50 border-slate-200' : 'hover:bg-blue-50/50 border-transparent hover:border-blue-100 group'}`}>
                                    <div className="w-10 h-10 rounded-full bg-[#0077b5] text-white flex items-center justify-center shrink-0">
                                        <Linkedin size={20} fill="currentColor" className="text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">LinkedIn</div>
                                        {isEditingContact ? (
                                            <input
                                                type="text"
                                                value={editedContact.linkedin_url}
                                                onChange={(e) => setEditedContact({ ...editedContact, linkedin_url: e.target.value })}
                                                className="w-full px-2 py-1 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0077b5]"
                                                placeholder="linkedin.com/in/..."
                                            />
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                {profile.contactInfo?.linkedin_url ? (
                                                    <a
                                                        href={profile.contactInfo?.linkedin_url.startsWith('http') ? profile.contactInfo.linkedin_url : `https://${profile.contactInfo.linkedin_url}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-700 font-medium underline-offset-2 group-hover:underline flex items-center gap-1"
                                                    >
                                                        Voir le profil <ExternalLink size={14} />
                                                    </a>
                                                ) : (
                                                    <span className="text-slate-400 text-sm">Non renseigné</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                                {isEditingContact ? (
                                    <>
                                        <button
                                            onClick={() => setIsEditingContact(false)}
                                            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                                            disabled={isSavingContact}
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={onSaveContact}
                                            disabled={isSavingContact}
                                            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-bold hover:bg-amber-700 transition-colors shadow-sm flex items-center gap-2"
                                        >
                                            {isSavingContact ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                            Enregistrer
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setIsContactModalOpen(false)}
                                        className="px-6 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 hover:text-slate-900 transition-colors shadow-sm"
                                    >
                                        Fermer
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
    );
};

export default CandidateDetailPanel;
